/**
 * Notificações sonoras do inbox: preferências e detecção de eventos.
 *
 * Módulo PURO de propósito — nenhum React, nenhum Web Audio. O que toca som
 * mora no `useNotificacaoSom`; aqui fica só a decisão de QUANDO tocar, que é a
 * parte que dá para testar sem navegador e sem alto-falante.
 *
 * ## Por que `localStorage` aqui, se o `estadoDeSessao` proíbe
 *
 * O `lib/estadoDeSessao.js` guarda estado EM MEMÓRIA e diz, em letras
 * garrafais, para nunca usar `localStorage` — porque o que ele guarda é agenda
 * de paciente: nome e telefone, que não podem ficar escritos no disco de um
 * computador compartilhado (PLANO seção 9 — LGPD).
 *
 * Aqui é o oposto: o que se grava são TRÊS BOOLEANOS de preferência de som.
 * Não há dado de paciente, e a preferência PRECISA sobreviver ao reload — quem
 * ligou o som ontem não quer religá-lo a cada F5. A chave inclui o uid porque o
 * computador da recepção é compartilhado: a preferência de uma pessoa não pode
 * valer para quem entrar depois.
 */

export const EVENTO_AGUARDANDO = 'aguardando';
export const EVENTO_MENSAGEM = 'mensagem';

/**
 * Os eventos, na ordem em que são exibidos e em que os sons tocam.
 *
 * `descricao` não é enfeite: som é notificação sem texto, e quem liga o toggle
 * precisa saber o que vai apitar antes de ouvir pela primeira vez.
 */
export const EVENTOS = [
  {
    id: EVENTO_AGUARDANDO,
    label: 'Novo no Aguardando',
    descricao: 'Uma conversa entra na fila sem responsável — inclusive ao reabrir.',
  },
  {
    id: EVENTO_MENSAGEM,
    label: 'Nova mensagem de paciente',
    descricao: 'Chega mensagem numa conversa que você não está lendo agora.',
  },
];

const ORDEM = EVENTOS.map((e) => e.id);

/**
 * Geral DESLIGADO até a pessoa habilitar.
 *
 * Não é timidez de produto: o autoplay dos navegadores só libera áudio depois
 * de um gesto do usuário. Um padrão "ligado" prometeria som que o navegador
 * engoliria calado até o primeiro clique — e a pessoa concluiria que o recurso
 * está quebrado. Ligar é o gesto que também destrava o áudio.
 *
 * Os eventos, esses, nascem ligados: quem aciona o interruptor geral quer som,
 * não quer descobrir que precisa ligar mais dois.
 */
export const PREFERENCIAS_PADRAO = Object.freeze({
  geral: false,
  eventos: Object.freeze({ [EVENTO_AGUARDANDO]: true, [EVENTO_MENSAGEM]: true }),
});

export const chaveDaPreferencia = (uid) => `odontosdr:notif-som:${uid ?? 'anonimo'}`;

/**
 * O `localStorage` do navegador, ou `null` quando ele não existe ou está
 * bloqueado.
 *
 * Só tocar em `globalThis.localStorage` já lança em navegador com cookies de
 * terceiros bloqueados e em aba anônima de alguns navegadores — por isso o
 * acesso inteiro está dentro do `try`, não só a leitura.
 */
function deposito() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Normaliza qualquer coisa vinda do disco para a forma esperada. */
function sanear(bruto) {
  const eventos = { ...PREFERENCIAS_PADRAO.eventos };
  for (const id of ORDEM) {
    if (typeof bruto?.eventos?.[id] === 'boolean') eventos[id] = bruto.eventos[id];
  }
  return { geral: bruto?.geral === true, eventos };
}

/**
 * Lê as preferências da pessoa. NUNCA lança: falha vira o padrão.
 *
 * `armazem` existe para o teste injetar um depósito falso — em Node não há
 * `localStorage`, e stubar o global obrigaria todo teste a desfazer o stub.
 */
export function carregarPreferencias(uid, armazem = deposito()) {
  try {
    const cru = armazem?.getItem(chaveDaPreferencia(uid));
    if (!cru) return sanear(null);
    return sanear(JSON.parse(cru));
  } catch {
    // JSON corrompido por edição manual, cota estourada, storage bloqueado: em
    // todos os casos a resposta certa é o padrão, e em silêncio. Preferência de
    // som não é motivo para tela de erro.
    return sanear(null);
  }
}

/** Grava as preferências. Devolve `false` quando o navegador não deixou. */
export function salvarPreferencias(uid, preferencias, armazem = deposito()) {
  try {
    armazem?.setItem(chaveDaPreferencia(uid), JSON.stringify(sanear(preferencias)));
    return Boolean(armazem);
  } catch {
    return false;
  }
}

/** Aceita lista, `Map` ou objeto indexado — o provider entrega lista. */
function listaDe(snapshot) {
  if (!snapshot) return [];
  if (Array.isArray(snapshot)) return snapshot;
  if (typeof snapshot.values === 'function') return [...snapshot.values()];
  return Object.values(snapshot);
}

const indexar = (snapshot) => new Map(listaDe(snapshot).map((c) => [c?.id, c]));

/**
 * O balde "Aguardando", exatamente como o `agruparPorEstado` o define.
 *
 * Duplicado aqui de propósito? Não: é a MESMA regra, e por isso a ordem das
 * checagens é a mesma — grupo não é fila de espera, resolvida não é fila de
 * espera, e só então "sem responsável".
 */
export const noAguardando = (c) =>
  c?.ehGrupo !== true && c?.status !== 'resolvida' && !c?.responsavelId;

/**
 * Os ids que ENTRARAM no balde aguardando entre dois snapshots.
 *
 * Exportado porque o hook precisa exatamente disto para alimentar
 * `chegadasRecentes` — e duplicar a regra lá seria abrir a porta para as duas
 * cópias divergirem no primeiro ajuste.
 */
export function entraramNoAguardando(anterior, atual) {
  if (anterior === null || anterior === undefined) return [];
  const antes = indexar(anterior);
  const ids = [];
  for (const c of listaDe(atual)) {
    if (!c?.id) continue;
    const previa = antes.get(c.id);
    if (noAguardando(c) && !(previa && noAguardando(previa))) ids.push(c.id);
  }
  return ids;
}

/**
 * Compara dois snapshots e diz QUAIS tipos de evento merecem som.
 *
 * @param anterior  snapshot anterior, ou `null`/`undefined` no primeiro.
 * @param atual     snapshot recém-chegado.
 * @param opcoes    `abertaId` — a conversa aberta na tela agora.
 *                  `chegadasRecentes` — `Set` de ids que acabaram de entrar no
 *                  aguardando. Ver "a chegada leva TRÊS snapshots", abaixo.
 *                  `uid` — aceito e hoje ignorado; é o gancho do evento de
 *                  menção em nota interna, que não dá para detectar sem um dado
 *                  que a lista ainda não carrega (ver o relatório da leva).
 * @returns array de ids de evento, na ordem de `EVENTOS`, SEM repetição.
 *
 * ## O primeiro snapshot é sempre mudo
 *
 * Sem `anterior` não há mudança — há chegada. Abrir o inbox com 40 conversas em
 * espera dispararia uma rajada de sons que não corresponde a nada que acabou de
 * acontecer. `anterior` vazio (`[]`) é diferente de ausente: a lista já esteve
 * carregada e estava vazia, então o que entrar agora é novidade de verdade.
 *
 * ## A chegada de um paciente leva TRÊS snapshots
 *
 * Medido no navegador contra o Firestore, não deduzido:
 *
 *   1. a conversa aparece — `naoLidas: 0`, `ultimaMensagem: null`;
 *   2. `ultimaMensagem` é preenchida, `naoLidas` AINDA 0;
 *   3. `naoLidas` vira 1.
 *
 * Uma pessoa, uma mensagem, três atualizações. Sem defesa, o passo 1 toca o som
 * de aguardando e o passo 3 toca o de mensagem: dois sons diferentes para o
 * mesmo fato, com um segundo de intervalo.
 *
 * Por isso `chegadasRecentes`. A defesa não cabe aqui dentro porque atravessa
 * snapshots, e esta função compara só dois — quem guarda a memória é o
 * `useNotificacaoSom`. Mantê-la como PARÂMETRO preserva a pureza: mesma
 * entrada, mesma saída.
 *
 * ## Um som por TIPO, por snapshot
 *
 * Dez conversas entrando juntas são um evento para o ouvido humano, não dez.
 * O disparo em lote da confirmação de agenda move dezenas de conversas para
 * Aguardando de uma vez; sem este teto, a recepção ouviria uma metralhadora.
 */
export function detectarEventos(anterior, atual, opcoes = {}) {
  if (anterior === null || anterior === undefined) return [];

  const { abertaId = null, chegadasRecentes = null } = opcoes;
  const antes = indexar(anterior);
  const disparados = new Set();

  for (const c of listaDe(atual)) {
    if (!c?.id) continue;
    const previa = antes.get(c.id);

    /**
     * ENTRAR no balde — não "estar" nele.
     *
     * Cobre a conversa que nasceu agora e também a que voltou: encerrada que
     * reabre, ou em atendimento que perdeu o responsável (transferência
     * devolvida, disparo de agenda). Todas significam a mesma coisa para quem
     * ouve: tem gente esperando e ninguém pegou.
     */
    if (noAguardando(c) && !(previa && noAguardando(previa))) {
      disparados.add(EVENTO_AGUARDANDO);
    }

    /**
     * Mensagem nova exige as DUAS pontas da comparação.
     *
     * Sem `previa` não existe "aumentou" — e a conversa que acabou de nascer já
     * foi anunciada pelo evento de cima. Contá-la aqui também faria dois sons
     * para um fato só.
     *
     * `chegadasRecentes` é o que cala o segundo som da MESMA chegada — a
     * conversa que acabou de entrar no aguardando não apita de novo quando o
     * `naoLidas` dela sobe logo em seguida. Vale também para a conversa
     * encerrada que reabre: reabrir e receber a mensagem são um fato só.
     *
     * ⚠ GRUPO fica de fora. O evento se chama "mensagem de paciente", e grupo
     * tem seção própria no inbox justamente por não ser atendimento de uma
     * pessoa. Os grupos da clínica conversam o dia inteiro (a importação do
     * histórico descartou ~5 mil mensagens de grupo em 90 dias): apitar em cada
     * uma faria a recepção desligar o recurso inteiro no primeiro dia.
     */
    if (previa && c.ehGrupo !== true && c.id !== abertaId && !chegadasRecentes?.has(c.id)) {
      if (Number(c.naoLidas ?? 0) > Number(previa.naoLidas ?? 0)) {
        disparados.add(EVENTO_MENSAGEM);
      }
    }
  }

  return ORDEM.filter((id) => disparados.has(id));
}

/** Filtra o que a pessoa habilitou. Geral desligado cala tudo. */
export function eventosAudiveis(eventos, preferencias) {
  const prefs = sanear(preferencias);
  if (!prefs.geral) return [];
  return (eventos ?? []).filter((id) => prefs.eventos[id] === true);
}
